import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Modal } from '@/components/modals/Modal/Modal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { COLORS } from '@/styles/tokens/colors';

interface ImprovePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImprove: (comments?: string) => void;
  loading?: boolean;
  isCreate?: boolean;
}

export function ImprovePromptModal({
  isOpen,
  onClose,
  onImprove,
  loading,
  isCreate = false,
}: ImprovePromptModalProps) {
  const { showFeedback } = useFeedback();
  const [includeComments, setIncludeComments] = useState(false);
  const [comments, setComments] = useState('');

  const handleImprove = useCallback(() => {
    if (isCreate) {
      if (!comments) {
        showFeedback('Please describe what you want this prompt to do');
        return;
      }
      onImprove(comments);
    } else {
      onImprove(includeComments ? comments : undefined);
    }
  }, [isCreate, comments, onImprove, showFeedback, includeComments]);

  const handleClose = useCallback(() => {
    setIncludeComments(false);
    setComments('');
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      heading={isCreate ? 'Create prompt' : 'Improve prompt'}
      rightHeaderContent={
        <CloseButton variant="transparent" size="xs" onClick={handleClose} />
      }
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={handleImprove}
          loading={loading}
          startIcon={Stars01Icon}
        >
          {isCreate ? 'Create prompt' : 'Improve prompt'}
        </Button>
      }
    >
      {isCreate ? (
        <Stack spacing={2}>
          <Typography variant="body-s">
            <span>
              Luminary AI will help you create a prompt based on your
              requirements.
            </span>
            <Box component="ul" sx={{ pl: 3, mt: 0.5 }}>
              <li>
                Specify what information should be extracted or summarized
              </li>
              <li>Describe the target audience of the document</li>
              <li>Define any specific terminology or style guidelines</li>
            </Box>
          </Typography>

          <TextInput
            label="Document summary requirements"
            multiline
            rows={6}
            placeholder="Describe what you want this prompt to do..."
            value={comments}
            required
            onChange={(e) => setComments(e.target.value)}
            fullWidth
          />
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body-s">
            Luminary AI will identify opportunities to optimize this prompt. You
            may also describe any modifications that you&apos;d like to see
            below.
          </Typography>

          <Stack
            spacing={2}
            p={2}
            sx={{
              backgroundColor: COLORS.GRAY[50],
              borderRadius: 1,
            }}
          >
            <Switch
              label="Describe additional changes"
              helpText="Describe the modifications or improvements you would like to make to this prompt"
              labelPosition="right"
              value={includeComments}
              onChange={(e) => setIncludeComments(e.target.checked)}
            />

            {includeComments && (
              <Stack spacing={1}>
                <TextInput
                  label="Additional prompt changes"
                  hideLabel
                  multiline
                  rows={6}
                  placeholder="Add more details on..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  fullWidth
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </Modal>
  );
}
